/**
 * Occupational synonym expansion.
 *
 * @see ../docs/search-pipeline.md — §3.2 (synonym stage), §4 (title-boost),
 *      and §5.1 (South-African trade vocabulary + directed car→mechanic rule).
 *      Curated groups here are merged over WordNet at load time.
 *
 * OFO descriptions are formal ("Motor Vehicle Mechanic") and never contain the
 * casual words job-seekers actually type ("cars", "fix", "builder"). This module
 * maps those casual/colloquial terms to the formal vocabulary so that:
 *   - the compiler can bake synonyms into each document's indexed + embedded text
 *     (fully offline lexical + vector retrieval), and
 *   - the runtime can expand query tokens before BM25 matching (belt-and-suspenders).
 *
 * Groups are mutually synonymous within an occupational context. Compiled once
 * into a flat `EXPAND` lookup. Pure data + logic — safe for the browser bundle.
 */
const SYNONYM_GROUPS = [
    // Transport / vehicles. NOTE: deliberately excludes "mechanic"/"mechanics"
    // so that typing "mechanic" does NOT flood every car/vehicle word (which would
    // bury the actual car mechanic among all mechanic trades). The reverse link
    // (car/vehicle/automotive/motor → mechanic) is added below as a directed rule.
    ['car', 'cars', 'automobile', 'automobiles', 'auto', 'motorcar', 'motor', 'motor vehicle', 'motor vehicles', 'vehicle', 'vehicles', 'automotive'],
    ['truck', 'trucks', 'lorry', 'lorries', 'hgv'],
    ['motorcycle', 'motorcycles', 'motorbike', 'motorbikes', 'bike', 'bikes'],
    ['bus', 'buses', 'coach', 'coaches'],
    ['diesel', 'diesel engine', 'diesel engines'],
    ['panel beater', 'panel beaters', 'panelbeater', 'panelbeaters'],
    ['engine', 'engines', 'motor', 'motors'],
    ['tyre', 'tyres', 'tire', 'tires'],
    ['battery', 'batteries'],
    ['petrol', 'gasoline', 'gas'],
    // Repair / maintenance verbs
    ['fix', 'fixes', 'fixed', 'fixing', 'repair', 'repairs', 'repairing', 'repairer', 'repairers', 'maintain', 'maintains', 'maintaining', 'maintenance', 'mend', 'mends', 'overhaul', 'recondition'],
    // Generalist / handyman (OFO has "Handyperson"; casual terms must resolve to it)
    ['handyman', 'handymen', 'handy man', 'handyperson', 'odd jobs', 'odd job', 'fixer', 'labourer', 'labourers', 'laborer'],
    // Outdoor / garden (casual "mow the lawn" type queries)
    ['mow', 'mowing', 'lawn', 'lawns', 'grass', 'grass cutting', 'yard', 'yard work', 'garden', 'grounds', 'landscape', 'groundskeeper'],
    ['weld', 'welds', 'welding', 'welder', 'fabricate', 'fabricates', 'fabrication', 'boilermaker', 'boiler', 'boilers'],
    // Construction / building
    ['build', 'builds', 'building', 'construct', 'constructs', 'construction'],
    ['bricklayer', 'bricklayers', 'brick', 'bricks', 'mason', 'masonry'],
    ['carpenter', 'carpenters', 'woodwork', 'joiner', 'joinery'],
    ['plumber', 'plumbers', 'plumbing', 'pipe', 'pipes', 'piping'],
    ['electrician', 'electricians', 'electrical', 'wire', 'wiring'],
    ['painter', 'painters', 'paint', 'painting', 'decorator', 'decorating'],
    ['roof', 'roofs', 'roofing', 'roofer', 'roofers'],
    ['concrete', 'cement', 'screed'],
    // General construction worker (no direct OFO code; map to trades + Builder's Worker)
    ['construction', 'construct', 'constructs', 'construction worker', 'construction workers', 'construction labourer', 'construction laborer', 'general construction', 'builders worker', 'builders labourer', 'bricklayer', 'bricklayers', 'carpenter', 'carpenters', 'painter', 'painters', 'plasterer', 'plasterers', 'tiler', 'tilers', 'roofer', 'roofers', 'plumber', 'plumbers', 'concreter', 'concretors'],
    // Health / medical / care
    ['nurse', 'nurses', 'nursing', 'caregiver', 'caregivers', 'carer', 'carers', 'care worker', 'care workers'],
    ['doctor', 'doctors', 'physician', 'medic', 'medical'],
    ['hospital', 'hospitals', 'clinic', 'clinics'],
    ['patient', 'patients', 'sick', 'ill'],
    ['paramedic', 'paramedics', 'ambulance'],
    ['pharmacist', 'pharmacy', 'dispensary'],
    ['clean', 'cleans', 'cleaning', 'cleaner', 'cleaners', 'hygiene', 'sanitise', 'sanitize'],
    // Food / cooking
    ['cook', 'cooks', 'cooking', 'chef', 'chefs', 'kitchen'],
    ['bake', 'bakes', 'baking', 'baker', 'bakers'],
    ['waiter', 'waiters', 'waitress', 'server', 'servers', 'food service'],
    ['barista', 'coffee'],
    ['food', 'foods', 'meal', 'meals', 'catering'],
    // Education / teaching / training
    ['teach', 'teaches', 'teaching', 'teacher', 'teachers', 'tutor', 'tutors', 'tutoring', 'educator', 'instructor', 'instructors'],
    ['train', 'trains', 'training', 'trainer', 'coach', 'coaches'],
    ['school', 'schools', 'classroom', 'lecture', 'lecturer'],
    // Retail / sales
    ['shop', 'shops', 'store', 'stores', 'retail', 'sell', 'sells', 'selling', 'sales', 'salesperson', 'clerk'],
    ['market', 'marketing'],
    // IT / tech
    ['computer', 'computers', 'it', 'tech', 'technology', 'software', 'hardware'],
    ['code', 'codes', 'coding', 'programmer', 'programmers', 'developer', 'developers', 'software engineer'],
    ['network', 'networks', 'networking', 'system', 'systems', 'administrator'],
    ['data', 'database', 'databases', 'analyst'],
    // Office / admin / finance
    ['admin', 'administrator', 'administrative', 'secretary', 'receptionist', 'reception', 'clerical', 'office'],
    ['account', 'accounts', 'accountant', 'accounting', 'bookkeeping', 'finance', 'financial', 'fiscals', 'bursar', 'bursars', 'bursary'],
    // Hair / beauty
    ['hair', 'hairdresser', 'hairdressers', 'barber', 'barbers', 'salon', 'beautician', 'beauty', 'stylist'],
    ['makeup', 'cosmetologist'],
    // Security
    ['guard', 'guards', 'security', 'protect', 'protection', 'police', 'policing'],
    ['fire', 'firefighter', 'firefighters', 'firefighting'],
    // Agriculture / farming / garden
    ['farm', 'farms', 'farmer', 'farmers', 'farming', 'agriculture'],
    ['crop', 'crops', 'plant', 'plants', 'planting'],
    ['animal', 'animals', 'livestock', 'cattle', 'poultry'],
    ['garden', 'gardener', 'gardeners', 'gardening', 'landscape'],
    // Cleaning / laundry
    ['janitor', 'housekeeping', 'housekeeper', 'laundry', 'wash', 'washing'],
    // Childcare
    ['child', 'children', 'childcare', 'babysit', 'babysitter', 'nanny', 'nursery'],
    // General work verbs
    ['make', 'makes', 'making', 'manufacture', 'manufactures', 'manufacturing', 'produce', 'produces', 'production'],
    ['help', 'helps', 'helping', 'assist', 'assistance', 'support'],
    ['drive', 'drives', 'driving', 'operate', 'operates', 'operating', 'chauffeur'],
    ['cut', 'cuts', 'cutting'],
    ['design', 'designs', 'designer', 'draft', 'drafting'],
    // Contractor-role casual phrasing → canonical OFO occupation tokens. These map
    // the everyday terms job-seekers type (which often have no exact OFO title,
    // e.g. "labourer", "general worker", "bobcat operator") to the real occupation.
    ['general worker', 'general workers', 'labourer', 'labourers', 'builders worker'],
    ['caregiver', 'carers', 'caretaker'],
    ['admin clerk', 'administrative clerk', 'clerk', 'general clerk'],
    ['steelworker', 'steel worker', 'steel fixer', 'steel erector'],
    ['instrumentation technician', 'instrument technician', 'instrument mechanician'],
    ['auto electrician', 'automotive electrician', 'vehicle electrician'],
    ['paver', 'pavers', 'paving'],
    ['bobcat', 'bobcat operator', 'loader operator'],
    ['code 14', 'code 14 driver', 'heavy truck driver'],
    ['bartender', 'bar tender', 'bar attendant'],
    ['dishwasher', 'kitchen hand', 'kitchenhand', 'kitchen assistant', 'catering assistant', 'kitchen steward'],
    ['drywall', 'drywall installer', 'plasterer'],
    ['shopfitter', 'shop fitter', 'carpenter and joiner'],
    ['roadworker', 'road worker', 'road construction', 'paving and surfacing worker'],
    ['car wash', 'vehicle wash', 'car cleaning', 'cleaner'],
    ['social media manager', 'social media', 'advertising and public relations manager'],
    ['waterproofer', 'waterproofing', 'damp proofer', 'insulation installer'],
];
const EXPAND = (() => {
    const map = new Map();
    for (const group of SYNONYM_GROUPS) {
        for (const word of group) {
            const key = word.toLowerCase();
            const others = group.filter((w) => w.toLowerCase() !== key).map((w) => w.toLowerCase());
            const existing = map.get(key) ?? [];
            for (const o of others)
                if (!existing.includes(o))
                    existing.push(o);
            map.set(key, existing);
        }
    }
    // Directed rule: car / vehicle / automotive / motor terms imply "mechanic"
    // (so "cars" / "vehicle" surface the motor mechanic), but "mechanic" does NOT
    // imply them — keeping the reverse link off prevents every mechanic trade
    // (bicycle, tractor, refrigeration, …) from absorbing car/vehicle tokens and
    // burying the actual Automotive Motor Mechanic (653101) under a BM25 tie.
    for (const t of [
        'car', 'cars', 'automobile', 'automobiles', 'auto', 'motorcar',
        'motor', 'motor vehicle', 'motor vehicles', 'vehicle', 'vehicles', 'automotive',
    ]) {
        const cur = map.get(t) ?? [];
        for (const m of ['mechanic', 'mechanics'])
            if (!cur.includes(m))
                cur.push(m);
        map.set(t, cur);
    }
    return map;
})();
function tokenise(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((t) => t.length > 0);
}
/**
 * Append synonym terms for every known token in `text`. Both the original token
 * and its synonyms are preserved, so canonical and casual forms all match.
 *
 * `map` is the active expansion table. At compile time this is derived from
 * WordNet (a proper lexical database) and baked into the index; at runtime the
 * baked table is passed in. When `map` is omitted (or empty) the curated
 * `GROUP_EXPAND` fallback is used so the pipeline still works without WordNet.
 */
export function expandText(text, map) {
    const table = map && Object.keys(map).length ? map : GROUP_EXPAND;
    const seen = new Set();
    const out = [];
    const push = (t) => {
        const k = t.toLowerCase();
        if (!seen.has(k)) {
            seen.add(k);
            out.push(k);
        }
    };
    for (const token of tokenise(text)) {
        push(token);
        // Guard against inherited Object.prototype keys (e.g. "constructor") that
        // may legitimately appear in document/query text.
        if (Object.prototype.hasOwnProperty.call(table, token)) {
            const synonyms = table[token];
            if (synonyms)
                for (const s of synonyms)
                    push(s);
        }
    }
    return out.join(' ');
}
/** Alias kept for call-site clarity at query time. */
export const expandQuery = expandText;
/** Curated fallback expansion table (used only when WordNet is unavailable). */
export const GROUP_EXPAND = Object.fromEntries([...EXPAND.entries()]);
//# sourceMappingURL=synonyms.js.map